import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import portalAPI, { portalShippingPreferencesAPI, portalDeliveryScheduleAPI, portalMilkRunAPI } from '../../services/portalAPI';
import PortalFooter from '../../components/Layout/PortalFooter';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import '../../styles/portal-brand.css';

interface DeliverySettings {
  max_supersack_lbs: number;
  minimum_shipment_lbs: number;
}

interface ShippingPreferences {
  preferred_shipping_method: string | null;
  fallback_shipping_method: string | null;
  has_loading_dock: boolean;
  can_receive_53ft_trailer: boolean;
  preferred_terminal: string;
  delivery_contact_name: string;
  delivery_contact_phone: string;
  delivery_notes: string;
}

interface DeliverySchedule {
  delivery_day: string | null;
  delivery_days_display?: string;
  is_daily?: boolean;
  ship_day: string | null;
  cutoff_day: string | null;
  next_delivery_date: string | null;
  next_delivery_formatted: string | null;
  ship_by_date: string | null;
  ship_by_formatted: string | null;
  order_cutoff_date: string | null;
  order_cutoff_formatted: string | null;
  carrier: string | null;
  can_make_nearest: boolean | null;
  days_until_delivery?: number;
  message?: string;
}

interface MilkRunInfo {
  milk_run_capable: boolean;
  on_milk_run?: boolean;
  milk_run_name?: string;
  milk_run_date?: string;
  stop_number?: number;
  total_stops?: number;
  status?: string;
  status_display?: string;
  is_booked?: boolean;
  region?: string;
  region_name?: string;
  others_in_region?: number;
  message?: string;
  offload_equipment?: string;
  truck_access?: boolean;
  requirements_met?: {
    equipment: boolean;
    truck_access: boolean;
  };
}

interface PriceBookEntry {
  id: number;
  product_type: string;
  product_detail: string;
  channel: string;
  channel_display: string;
  unit_type: string;
  price_per_unit: string;
  use_count: number;
  last_used: string;
}

const SHIPPING_METHOD_OPTIONS = [
  { value: 'will_call', label: 'Will Call - Kettle Falls', description: 'Pick up your order at our location' },
  { value: 'ltl_dock', label: 'LTL Ship to Dock', description: 'Freight delivery to your loading dock' },
  { value: 'ltl_business', label: 'LTL Ship to Business', description: 'Freight delivery with liftgate' },
  { value: 'ltl_terminal', label: 'LTL Terminal Pickup', description: 'Pick up from freight terminal' },
  { value: 'milk_run', label: 'Milk Run Delivery', description: 'Our weekly truck route (if available in your area)' },
];

const SUPERSACK_OPTIONS = [
  { value: 500, label: '500 lbs', description: 'Small tractor/forklift' },
  { value: 800, label: '800 lbs', description: 'Standard tractor bucket' },
  { value: 1000, label: '1,000 lbs', description: 'Medium forklift' },
  { value: 1500, label: '1,500 lbs', description: 'Large forklift' },
  { value: 2000, label: '2,000 lbs', description: 'Heavy equipment' },
  { value: 2600, label: '2,600 lbs (Full Pallet)', description: 'Maximum capacity' },
];

const SHIPMENT_OPTIONS = [
  { value: 0, label: 'No minimum', description: 'Ship every order individually' },
  { value: 1000, label: '1,000 lbs (Half Pallet)', description: 'Recommended - good balance of frequency and savings' },
  { value: 2000, label: '2,000 lbs (Full Pallet)', description: 'Best freight rates - fewer, larger shipments' },
];

const PortalSettings: React.FC = () => {
  usePortalAuth();
  const [settings, setSettings] = useState<DeliverySettings | null>(null);
  const [shippingPrefs, setShippingPrefs] = useState<ShippingPreferences | null>(null);
  const [deliverySchedule, setDeliverySchedule] = useState<DeliverySchedule | null>(null);
  const [milkRunInfo, setMilkRunInfo] = useState<MilkRunInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingShipping, setSavingShipping] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Supersack custom weight state
  const [customWeight, setCustomWeight] = useState<string>('');
  const [showCustom, setShowCustom] = useState(false);
  // Minimum shipment custom weight state
  const [customShipment, setCustomShipment] = useState<string>('');
  const [showCustomShipment, setShowCustomShipment] = useState(false);
  // Price book state
  const [priceBook, setPriceBook] = useState<PriceBookEntry[]>([]);
  const [priceBookLoading, setPriceBookLoading] = useState(false);
  const [editingPrice, setEditingPrice] = useState<PriceBookEntry | null>(null);
  const [editPriceValue, setEditPriceValue] = useState('');

  useEffect(() => {
    loadSettings();
    loadShippingPreferences();
    loadDeliverySchedule();
    loadMilkRunInfo();
    loadPriceBook();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await portalAPI.get('/portal/delivery-settings/');
      setSettings(response.data);

      // Check if supersack value is a preset or custom
      const isSupersackPreset = SUPERSACK_OPTIONS.some(opt => opt.value === response.data.max_supersack_lbs);
      if (!isSupersackPreset && response.data.max_supersack_lbs) {
        setShowCustom(true);
        setCustomWeight(response.data.max_supersack_lbs.toString());
      }

      // Check if shipment minimum is a preset or custom
      const isShipmentPreset = SHIPMENT_OPTIONS.some(opt => opt.value === response.data.minimum_shipment_lbs);
      if (!isShipmentPreset && response.data.minimum_shipment_lbs) {
        setShowCustomShipment(true);
        setCustomShipment(response.data.minimum_shipment_lbs.toString());
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadShippingPreferences = async () => {
    try {
      const response = await portalShippingPreferencesAPI.get();
      setShippingPrefs(response.data);
    } catch (err: any) {
      // Don't show error - shipping prefs may not exist yet
      console.log('No shipping preferences found, using defaults');
      setShippingPrefs({
        preferred_shipping_method: null,
        fallback_shipping_method: null,
        has_loading_dock: false,
        can_receive_53ft_trailer: false,
        preferred_terminal: '',
        delivery_contact_name: '',
        delivery_contact_phone: '',
        delivery_notes: '',
      });
    }
  };

  const loadDeliverySchedule = async () => {
    try {
      const response = await portalDeliveryScheduleAPI.get();
      setDeliverySchedule(response.data);
    } catch (err: any) {
      console.log('No delivery schedule found');
      setDeliverySchedule(null);
    }
  };

  const loadMilkRunInfo = async () => {
    try {
      const response = await portalMilkRunAPI.getInfo();
      setMilkRunInfo(response.data);
    } catch (err: any) {
      console.log('No milk run info found');
      setMilkRunInfo(null);
    }
  };

  const loadPriceBook = async () => {
    try {
      setPriceBookLoading(true);
      const response = await portalAPI.get('/portal/price-book/');
      setPriceBook(response.data || []);
    } catch (err: any) {
      console.log('No price book found');
      setPriceBook([]);
    } finally {
      setPriceBookLoading(false);
    }
  };

  const updatePrice = async (id: number, newPrice: string) => {
    try {
      await portalAPI.patch(`/portal/price-book/${id}/`, { price_per_unit: newPrice });
      setEditingPrice(null);
      setEditPriceValue('');
      loadPriceBook();
      setSuccess('Price updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Failed to update price');
    }
  };

  const deletePrice = async (id: number) => {
    try {
      await portalAPI.delete(`/portal/price-book/${id}/`);
      loadPriceBook();
      setSuccess('Price removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError('Failed to remove price');
    }
  };

  const saveShippingPreferences = async () => {
    if (!shippingPrefs) return;

    try {
      setSavingShipping(true);
      setError('');
      setSuccess('');
      await portalShippingPreferencesAPI.update(shippingPrefs);
      setSuccess('Shipping preferences saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save shipping preferences');
    } finally {
      setSavingShipping(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;

    // If custom is selected, use custom value for supersack
    const weightToSave = showCustom ? parseInt(customWeight) : settings.max_supersack_lbs;
    // If custom is selected, use custom value for shipment minimum
    const shipmentToSave = showCustomShipment ? parseInt(customShipment) : settings.minimum_shipment_lbs;

    if (isNaN(weightToSave) || weightToSave < 100 || weightToSave > 2600) {
      setError('Please enter a valid supersack weight between 100 and 2,600 lbs');
      return;
    }

    if (isNaN(shipmentToSave) || shipmentToSave < 0 || shipmentToSave > 50000) {
      setError('Please enter a valid shipment minimum between 0 and 50,000 lbs');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const response = await portalAPI.patch('/portal/delivery-settings/update_settings/', {
        max_supersack_lbs: weightToSave,
        minimum_shipment_lbs: shipmentToSave
      });
      setSettings(response.data);
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const selectOption = (value: number) => {
    if (!settings) return;
    setShowCustom(false);
    setSettings({ ...settings, max_supersack_lbs: value });
  };

  const selectCustom = () => {
    setShowCustom(true);
    if (!customWeight && settings) {
      setCustomWeight(settings.max_supersack_lbs.toString());
    }
  };

  const selectShipmentOption = (value: number) => {
    if (!settings) return;
    setShowCustomShipment(false);
    setSettings({ ...settings, minimum_shipment_lbs: value });
  };

  const selectCustomShipment = () => {
    setShowCustomShipment(true);
    if (!customShipment && settings) {
      setCustomShipment(settings.minimum_shipment_lbs.toString());
    }
  };

  if (loading) {
    return (
      <div className="portal-page">
        <div className="portal-container">
          <div className="portal-loading">Loading settings...</div>
        </div>
        <PortalFooter />
      </div>
    );
  }

  return (
    <div className="portal-page">
      <TutorialAutoStart pageKey="settings" />
      <div className="portal-container">
        <div className="portal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>Settings</h1>
            <p className="portal-subtitle">Manage your account preferences and equipment settings</p>
          </div>
          <TutorialHelpButton pageKey="settings" />
        </div>

        {error && <div className="portal-error">{error}</div>}
        {success && <div className="portal-success">{success}</div>}

        {settings && (
          <div className="settings-container">
            {/* Equipment & Delivery Section */}
            <div className="settings-section">
              <h2 className="section-title">Equipment & Delivery Preferences</h2>
              <p className="section-description">
                Help us size your supersack orders correctly for your equipment
              </p>

              <div className="setting-group">
                <div className="setting-label">
                  <strong>Maximum Supersack Weight</strong>
                  <p className="setting-help">What's the largest supersack your equipment can safely handle?</p>
                </div>

                <div className="supersack-options" data-tutorial="supersack-options">
                  {SUPERSACK_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={`supersack-option ${!showCustom && settings.max_supersack_lbs === option.value ? 'selected' : ''}`}
                      onClick={() => selectOption(option.value)}
                    >
                      <span className="option-value">{option.label}</span>
                      <span className="option-desc">{option.description}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`supersack-option ${showCustom ? 'selected' : ''}`}
                    onClick={selectCustom}
                  >
                    <span className="option-value">Custom</span>
                    <span className="option-desc">Enter a specific weight</span>
                  </button>
                </div>

                {showCustom && (
                  <div className="custom-weight-section">
                    <label htmlFor="custom-weight">Custom weight (lbs):</label>
                    <input
                      id="custom-weight"
                      type="number"
                      min="100"
                      max="2600"
                      step="50"
                      value={customWeight}
                      onChange={(e) => setCustomWeight(e.target.value)}
                      placeholder="Enter weight (100-2600)"
                    />
                    <span className="weight-hint">Minimum: 100 lbs, Maximum: 2,600 lbs</span>
                  </div>
                )}

                <div className="current-setting">
                  <span className="current-label">Current setting:</span>
                  <span className="current-value">
                    {showCustom ? (customWeight ? `${parseInt(customWeight).toLocaleString()} lbs` : 'Not set') : `${settings.max_supersack_lbs.toLocaleString()} lbs`}
                  </span>
                </div>
              </div>
            </div>

            {/* Shipping Optimization Section */}
            <div className="settings-section">
              <h2 className="section-title">Shipping Optimization</h2>
              <p className="section-description">
                Combine small orders into fuller loads to reduce freight costs
              </p>

              <div className="setting-group">
                <div className="setting-label">
                  <strong>Minimum Shipment Target</strong>
                  <p className="setting-help">Orders below this amount will be held until combined with the next order or until the delivery date arrives.</p>
                </div>

                <div className="shipment-options" data-tutorial="shipment-options">
                  {SHIPMENT_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={`shipment-option ${!showCustomShipment && settings.minimum_shipment_lbs === option.value ? 'selected' : ''}`}
                      onClick={() => selectShipmentOption(option.value)}
                    >
                      <span className="option-value">{option.label}</span>
                      <span className="option-desc">{option.description}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`shipment-option ${showCustomShipment ? 'selected' : ''}`}
                    onClick={selectCustomShipment}
                  >
                    <span className="option-value">Custom</span>
                    <span className="option-desc">Enter a specific weight target</span>
                  </button>
                </div>

                {showCustomShipment && (
                  <div className="custom-weight-section">
                    <label htmlFor="custom-shipment">Custom minimum (lbs):</label>
                    <input
                      id="custom-shipment"
                      type="number"
                      min="0"
                      max="50000"
                      step="1000"
                      value={customShipment}
                      onChange={(e) => setCustomShipment(e.target.value)}
                      placeholder="Enter weight (0-50,000)"
                    />
                    <span className="weight-hint">0 = no minimum, Maximum: 50,000 lbs</span>
                  </div>
                )}

                <div className="current-setting">
                  <span className="current-label">Current setting:</span>
                  <span className="current-value">
                    {showCustomShipment
                      ? (customShipment ? (parseInt(customShipment) === 0 ? 'No minimum' : `${parseInt(customShipment).toLocaleString()} lbs`) : 'Not set')
                      : (settings.minimum_shipment_lbs === 0 ? 'No minimum' : `${settings.minimum_shipment_lbs.toLocaleString()} lbs`)
                    }
                  </span>
                </div>

                <div className="info-box">
                  <strong>How this works:</strong>
                  <ul>
                    <li>Bag pallets: 40-65 bags per pallet (1,600-2,600 lbs)</li>
                    <li>Supersacks fit 1,000-2,600 lbs each</li>
                    <li>Half truck: ~12 pallets, Full truck: ~24 pallets</li>
                    <li>Orders are auto-combined for the same delivery week</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* LTL Delivery Schedule Card */}
            {deliverySchedule && deliverySchedule.delivery_day && (
              <div className="settings-section delivery-schedule-card">
                <h2 className="section-title">
                  <span style={{ marginRight: '0.5rem' }}>🚚</span>
                  Your LTL Delivery Schedule
                </h2>
                <div className="schedule-info">
                  <div className="schedule-carrier">
                    <span className="label">Carrier:</span>
                    <span className="value">{deliverySchedule.carrier}</span>
                  </div>
                  <div className="schedule-day">
                    <span className="label">Delivery day:</span>
                    <span className="value highlight">{deliverySchedule.delivery_day?.toUpperCase()}</span>
                  </div>

                  <div className="schedule-timeline">
                    <div className="timeline-step">
                      <span className="step-label">ORDER BY</span>
                      <span className="step-value">{deliverySchedule.order_cutoff_formatted}</span>
                    </div>
                    <span className="timeline-arrow">→</span>
                    <div className="timeline-step">
                      <span className="step-label">YOU RECEIVE</span>
                      <span className="step-value">{deliverySchedule.next_delivery_formatted}</span>
                    </div>
                  </div>

                  <div className="schedule-next">
                    <div className="next-item" style={{ fontSize: '13px', color: '#666' }}>
                      ~{deliverySchedule.days_until_delivery || 9} day lead time
                    </div>
                  </div>

                  {deliverySchedule.can_make_nearest === false && (
                    <div className="schedule-warning">
                      ⚠️ Lead time requires 9+ days. Order now for the next available delivery.
                    </div>
                  )}
                </div>
              </div>
            )}

            {deliverySchedule && !deliverySchedule.delivery_day && (
              <div className="settings-section delivery-schedule-card">
                <h2 className="section-title">
                  <span style={{ marginRight: '0.5rem' }}>🚚</span>
                  LTL Delivery Schedule
                </h2>
                <p className="schedule-message">
                  {deliverySchedule.message || "Your delivery schedule hasn't been configured yet."}
                </p>
                <p className="schedule-contact">
                  Questions? Call us at (509) 738-9004
                </p>
              </div>
            )}

            {/* Milk Run Info Card - show if customer prefers milk run or is on one */}
            {milkRunInfo && milkRunInfo.milk_run_capable && milkRunInfo.on_milk_run && (
              <div className="settings-section milk-run-card">
                <h2 className="section-title">
                  <span style={{ marginRight: '0.5rem' }}>🚛</span>
                  Your Upcoming Milk Run
                </h2>
                <div className="milk-run-info">
                  <div className="milk-run-detail">
                    <span className="label">Route:</span>
                    <span className="value highlight">{milkRunInfo.milk_run_name}</span>
                  </div>
                  <div className="milk-run-detail">
                    <span className="label">Date:</span>
                    <span className="value">{milkRunInfo.milk_run_date}</span>
                  </div>
                  <div className="milk-run-detail">
                    <span className="label">Your stop:</span>
                    <span className="value">#{milkRunInfo.stop_number} of {milkRunInfo.total_stops}</span>
                  </div>
                  <div className="milk-run-status">
                    <span className={`status-badge ${milkRunInfo.is_booked ? 'booked' : 'planning'}`}>
                      {milkRunInfo.status_display}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {milkRunInfo && milkRunInfo.milk_run_capable && !milkRunInfo.on_milk_run && milkRunInfo.region && (
              <div className="settings-section milk-run-card available">
                <h2 className="section-title">
                  <span style={{ marginRight: '0.5rem' }}>🚛</span>
                  Milk Run Delivery
                </h2>
                <div className="milk-run-info">
                  <p className="milk-run-eligible">
                    ✅ Your farm is set up for milk run delivery
                  </p>
                  <div className="milk-run-detail">
                    <span className="label">Region:</span>
                    <span className="value">{milkRunInfo.region_name}</span>
                  </div>
                  {milkRunInfo.others_in_region && milkRunInfo.others_in_region > 0 ? (
                    <p className="milk-run-note">
                      📦 {milkRunInfo.others_in_region} other orders in your region — a milk run may be coming soon!
                    </p>
                  ) : (
                    <p className="milk-run-note">
                      When we have enough orders in your area, we'll schedule a milk run. These are often more economical for larger orders!
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Shipping Method Preferences Section */}
            {shippingPrefs && (
              <div className="settings-section">
                <h2 className="section-title">Default Shipping Method</h2>
                <p className="section-description">
                  Set your preferred shipping method for new orders
                </p>

                <div className="setting-group">
                  <div className="setting-label">
                    <strong>Preferred Shipping Method</strong>
                    <p className="setting-help">This will be pre-selected when you place new orders</p>
                  </div>

                  <div className="shipping-method-options">
                    {SHIPPING_METHOD_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={`shipping-option ${shippingPrefs.preferred_shipping_method === option.value ? 'selected' : ''}`}
                        onClick={() => setShippingPrefs({ ...shippingPrefs, preferred_shipping_method: option.value })}
                      >
                        <span className="option-value">{option.label}</span>
                        <span className="option-desc">{option.description}</span>
                      </button>
                    ))}
                  </div>

                  {shippingPrefs.preferred_shipping_method === 'ltl_terminal' && (
                    <div className="conditional-field">
                      <label htmlFor="preferred-terminal">Preferred Freight Terminal:</label>
                      <input
                        id="preferred-terminal"
                        type="text"
                        value={shippingPrefs.preferred_terminal}
                        onChange={(e) => setShippingPrefs({ ...shippingPrefs, preferred_terminal: e.target.value })}
                        placeholder="e.g., Spokane Terminal"
                      />
                    </div>
                  )}

                  {(shippingPrefs.preferred_shipping_method === 'ltl_dock' ||
                    shippingPrefs.preferred_shipping_method === 'ltl_business' ||
                    shippingPrefs.preferred_shipping_method === 'milk_run') && (
                    <div className="delivery-capabilities">
                      <div className="capability-checkbox">
                        <input
                          type="checkbox"
                          id="has-loading-dock"
                          checked={shippingPrefs.has_loading_dock}
                          onChange={(e) => setShippingPrefs({ ...shippingPrefs, has_loading_dock: e.target.checked })}
                        />
                        <label htmlFor="has-loading-dock">
                          <strong>Loading dock available</strong>
                          <span>For freight truck unloading</span>
                        </label>
                      </div>
                      {shippingPrefs.preferred_shipping_method === 'milk_run' && (
                        <div className="capability-checkbox">
                          <input
                            type="checkbox"
                            id="can-receive-53ft"
                            checked={shippingPrefs.can_receive_53ft_trailer}
                            onChange={(e) => setShippingPrefs({ ...shippingPrefs, can_receive_53ft_trailer: e.target.checked })}
                          />
                          <label htmlFor="can-receive-53ft">
                            <strong>Can receive 53' trailer</strong>
                            <span>Required for milk run delivery</span>
                          </label>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="delivery-contact">
                    <div className="setting-label">
                      <strong>Delivery Contact (Optional)</strong>
                      <p className="setting-help">Who should freight carriers contact at delivery?</p>
                    </div>
                    <div className="contact-fields">
                      <input
                        type="text"
                        placeholder="Contact name"
                        value={shippingPrefs.delivery_contact_name}
                        onChange={(e) => setShippingPrefs({ ...shippingPrefs, delivery_contact_name: e.target.value })}
                      />
                      <input
                        type="tel"
                        placeholder="Contact phone"
                        value={shippingPrefs.delivery_contact_phone}
                        onChange={(e) => setShippingPrefs({ ...shippingPrefs, delivery_contact_phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="delivery-notes">
                    <label htmlFor="delivery-notes">Delivery Notes:</label>
                    <textarea
                      id="delivery-notes"
                      value={shippingPrefs.delivery_notes}
                      onChange={(e) => setShippingPrefs({ ...shippingPrefs, delivery_notes: e.target.value })}
                      placeholder="Special instructions for drivers (gate codes, best times, etc.)"
                      rows={3}
                    />
                  </div>

                  <button
                    className="portal-btn portal-btn-secondary"
                    onClick={saveShippingPreferences}
                    disabled={savingShipping}
                    style={{ marginTop: '1rem' }}
                  >
                    {savingShipping ? 'Saving...' : 'Save Shipping Preferences'}
                  </button>
                </div>
              </div>
            )}

            {/* Other Settings Links */}
            <div className="settings-section" data-tutorial="price-book">
              <h2 className="section-title">Your Prices</h2>
              <p className="section-description">
                Prices Laura has learned from your sales. She uses these to auto-fill when you don't mention a price.
              </p>

              {priceBookLoading ? (
                <p style={{ color: '#9ca3af', fontSize: 14 }}>Loading prices...</p>
              ) : priceBook.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: 14 }}>
                  No prices learned yet. Laura will start tracking your prices as you log sales.
                </p>
              ) : (
                <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#374151' }}>Product</th>
                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#374151' }}>Channel</th>
                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#374151' }}>Price</th>
                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#374151' }}>Unit</th>
                        <th style={{ padding: '8px 12px', fontWeight: 600, color: '#374151' }}>Last Used</th>
                        <th style={{ padding: '8px 6px', width: 60 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceBook.map(p => (
                        <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px 12px', textTransform: 'capitalize' }}>
                            {p.product_detail || p.product_type}
                          </td>
                          <td style={{ padding: '8px 12px' }}>{p.channel_display}</td>
                          <td style={{ padding: '8px 12px' }}>
                            {editingPrice?.id === p.id ? (
                              <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editPriceValue}
                                  onChange={e => setEditPriceValue(e.target.value)}
                                  style={{ width: 80, padding: '2px 6px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 13 }}
                                  autoFocus
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') updatePrice(p.id, editPriceValue);
                                    if (e.key === 'Escape') setEditingPrice(null);
                                  }}
                                />
                                <button
                                  onClick={() => updatePrice(p.id, editPriceValue)}
                                  style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12 }}
                                >Save</button>
                              </span>
                            ) : (
                              <span
                                onClick={() => { setEditingPrice(p); setEditPriceValue(p.price_per_unit); }}
                                style={{ cursor: 'pointer', borderBottom: '1px dashed #9ca3af' }}
                                title="Click to edit"
                              >
                                ${parseFloat(p.price_per_unit).toFixed(2)}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px' }}>/{p.unit_type}</td>
                          <td style={{ padding: '8px 12px', color: '#9ca3af', fontSize: 13 }}>
                            {p.last_used ? new Date(p.last_used).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                          </td>
                          <td style={{ padding: '8px 6px' }}>
                            <button
                              onClick={() => { if (window.confirm('Remove this price?')) deletePrice(p.id); }}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, padding: '2px 6px' }}
                              title="Remove price"
                            >&times;</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Other Settings Section */}
            <div className="settings-section">
              <h2 className="section-title">Other Settings</h2>

              <div className="settings-links">
                <Link to="/portal/email-preferences" className="settings-link">
                  <div className="link-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <div className="link-content">
                    <span className="link-title">Email Preferences</span>
                    <span className="link-desc">Manage which notifications you receive</span>
                  </div>
                  <span className="link-arrow">&rarr;</span>
                </Link>
                <Link to="/portal/change-password" className="settings-link">
                  <div className="link-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </div>
                  <div className="link-content">
                    <span className="link-title">Change Password</span>
                    <span className="link-desc">Update your account password</span>
                  </div>
                  <span className="link-arrow">&rarr;</span>
                </Link>
              </div>
            </div>

            <div className="settings-actions">
              <button
                className="portal-btn portal-btn-primary"
                onClick={saveSettings}
                disabled={saving}
                data-tutorial="save-settings"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
      <PortalFooter />

      <style>{`
        .settings-container {
          max-width: 700px;
        }

        .settings-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .section-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #2D5A27;
          margin: 0 0 0.25rem 0;
        }

        .section-description {
          color: #666;
          font-size: 0.875rem;
          margin: 0 0 1.5rem 0;
        }

        .setting-group {
          margin-bottom: 1rem;
        }

        .setting-label {
          margin-bottom: 1rem;
        }

        .setting-label strong {
          color: #333;
          font-size: 1rem;
        }

        .setting-help {
          color: #666;
          font-size: 0.875rem;
          margin: 0.25rem 0 0 0;
        }

        .supersack-options {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .supersack-option {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 1rem;
          background: #f9f9f9;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        }

        .supersack-option:hover {
          border-color: #2D5A27;
          background: #f5f5f5;
        }

        .supersack-option.selected {
          border-color: #2D5A27;
          background: #E8F5E9;
        }

        .option-value {
          font-weight: 600;
          color: #333;
          font-size: 1rem;
        }

        .supersack-option.selected .option-value {
          color: #2D5A27;
        }

        .option-desc {
          font-size: 0.75rem;
          color: #888;
          margin-top: 0.25rem;
        }

        /* Shipment options (similar to supersack but vertical layout) */
        .shipment-options {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .shipment-option {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 1rem;
          background: #f9f9f9;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        }

        .shipment-option:hover {
          border-color: #2D5A27;
          background: #f5f5f5;
        }

        .shipment-option.selected {
          border-color: #2D5A27;
          background: #E8F5E9;
        }

        .shipment-option.selected .option-value {
          color: #2D5A27;
        }

        .info-box {
          background: #FFF8E1;
          border: 1px solid #FFE082;
          border-radius: 8px;
          padding: 1rem;
          margin-top: 1rem;
        }

        .info-box strong {
          color: #F57C00;
          font-size: 0.875rem;
        }

        .info-box ul {
          margin: 0.5rem 0 0 0;
          padding-left: 1.25rem;
          font-size: 0.8rem;
          color: #666;
        }

        .info-box li {
          margin-bottom: 0.25rem;
        }

        .custom-weight-section {
          background: #f9f9f9;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .custom-weight-section label {
          display: block;
          font-weight: 500;
          color: #555;
          margin-bottom: 0.5rem;
        }

        .custom-weight-section input {
          width: 200px;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 1rem;
        }

        .custom-weight-section input:focus {
          outline: none;
          border-color: #2D5A27;
          box-shadow: 0 0 0 3px rgba(45, 90, 39, 0.1);
        }

        .weight-hint {
          display: block;
          font-size: 0.75rem;
          color: #888;
          margin-top: 0.5rem;
        }

        .current-setting {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: #E8F5E9;
          border-radius: 8px;
          margin-top: 1rem;
        }

        .current-label {
          color: #2D5A27;
          font-size: 0.875rem;
        }

        .current-value {
          font-weight: 600;
          color: #1B5E20;
          font-size: 1rem;
        }

        .settings-links {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .settings-link {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: #FAFAFA;
          border-radius: 8px;
          text-decoration: none;
          color: inherit;
          transition: background 0.2s;
        }

        .settings-link:hover {
          background: #F0F0F0;
        }

        .link-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #2D5A27;
          color: white;
          border-radius: 8px;
        }

        .link-content {
          flex: 1;
        }

        .link-title {
          display: block;
          font-weight: 500;
          color: #333;
        }

        .link-desc {
          display: block;
          font-size: 0.8rem;
          color: #888;
        }

        .link-arrow {
          color: #2D5A27;
          font-size: 1.25rem;
        }

        .settings-actions {
          margin-top: 1.5rem;
          display: flex;
          justify-content: flex-end;
        }

        .portal-success {
          background-color: #E8F5E9;
          color: #2E7D32;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          border: 1px solid #A5D6A7;
        }

        /* Shipping Method Options */
        .shipping-method-options {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .shipping-option {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 1rem;
          border: 2px solid #E0E0E0;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
          text-align: left;
        }

        .shipping-option:hover {
          border-color: #2D5A27;
          background: #FAFAFA;
        }

        .shipping-option.selected {
          border-color: #2D5A27;
          background: #E8F5E9;
        }

        .shipping-option .option-value {
          font-weight: 600;
          color: #333;
        }

        .shipping-option .option-desc {
          font-size: 0.85rem;
          color: #666;
          margin-top: 0.25rem;
        }

        .conditional-field {
          background: #F5F5F5;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .conditional-field label {
          display: block;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .conditional-field input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 1rem;
        }

        .delivery-capabilities {
          background: #F5F5F5;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .capability-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 0.5rem 0;
        }

        .capability-checkbox input[type="checkbox"] {
          width: 20px;
          height: 20px;
          margin-top: 2px;
          cursor: pointer;
        }

        .capability-checkbox label {
          cursor: pointer;
        }

        .capability-checkbox label strong {
          display: block;
          color: #333;
        }

        .capability-checkbox label span {
          display: block;
          font-size: 0.85rem;
          color: #666;
        }

        .delivery-contact {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #eee;
        }

        .contact-fields {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .contact-fields input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 1rem;
        }

        .delivery-notes {
          margin-top: 1rem;
        }

        .delivery-notes label {
          display: block;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .delivery-notes textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 1rem;
          resize: vertical;
        }

        .portal-btn-secondary {
          background: white;
          border: 2px solid #2D5A27;
          color: #2D5A27;
          padding: 0.75rem 1.5rem;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .portal-btn-secondary:hover:not(:disabled) {
          background: #E8F5E9;
        }

        .portal-btn-secondary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Delivery Schedule Card */
        .delivery-schedule-card {
          background: linear-gradient(135deg, #E8F5E9, #F1F8E9);
          border: 2px solid #2D5A27;
        }

        .delivery-schedule-card .section-title {
          display: flex;
          align-items: center;
        }

        .schedule-info {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .schedule-carrier, .schedule-day {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .schedule-carrier .label, .schedule-day .label {
          color: #555;
        }

        .schedule-carrier .value {
          font-weight: 500;
          color: #333;
        }

        .schedule-day .value.highlight {
          font-size: 1.25rem;
          font-weight: 700;
          color: #2D5A27;
        }

        .schedule-timeline {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1rem;
          background: white;
          border-radius: 8px;
          margin: 0.5rem 0;
        }

        .timeline-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0.5rem 1rem;
        }

        .timeline-step .step-label {
          font-size: 0.75rem;
          color: #888;
          text-transform: uppercase;
        }

        .timeline-step .step-value {
          font-weight: 600;
          color: #2D5A27;
        }

        .timeline-arrow {
          color: #2D5A27;
          font-size: 1.25rem;
          font-weight: bold;
        }

        .schedule-next {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
          padding: 1rem;
          background: rgba(255,255,255,0.7);
          border-radius: 8px;
        }

        .next-item {
          display: flex;
          flex-direction: column;
        }

        .next-item .label {
          font-size: 0.75rem;
          color: #666;
        }

        .next-item .value {
          font-weight: 500;
          color: #333;
        }

        .schedule-warning {
          padding: 0.75rem;
          background: #FFF3E0;
          border-radius: 6px;
          color: #E65100;
          font-size: 0.9rem;
        }

        .schedule-message {
          color: #666;
          margin-bottom: 0.5rem;
        }

        .schedule-contact {
          color: #2D5A27;
          font-weight: 500;
        }

        /* Milk Run Card */
        .milk-run-card {
          background: linear-gradient(135deg, #E3F2FD, #BBDEFB);
          border: 2px solid #1976D2;
        }

        .milk-run-card.available {
          background: linear-gradient(135deg, #F3E5F5, #E1BEE7);
          border: 2px solid #7B1FA2;
        }

        .milk-run-info {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .milk-run-detail {
          display: flex;
          gap: 0.5rem;
        }

        .milk-run-detail .label {
          color: #555;
        }

        .milk-run-detail .value {
          font-weight: 500;
          color: #333;
        }

        .milk-run-detail .value.highlight {
          font-weight: 700;
          color: #1565C0;
        }

        .milk-run-status {
          margin-top: 0.5rem;
        }

        .status-badge {
          display: inline-block;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge.booked {
          background: #C8E6C9;
          color: #2E7D32;
        }

        .status-badge.planning {
          background: #FFF9C4;
          color: #F57F17;
        }

        .milk-run-eligible {
          color: #2E7D32;
          font-weight: 500;
          margin: 0;
        }

        .milk-run-note {
          color: #555;
          font-size: 0.9rem;
          margin: 0.5rem 0 0 0;
          padding: 0.75rem;
          background: rgba(255,255,255,0.7);
          border-radius: 6px;
        }

        @media (max-width: 600px) {
          .settings-section {
            padding: 1rem;
          }

          .supersack-options {
            grid-template-columns: 1fr;
          }

          .custom-weight-section input {
            width: 100%;
          }

          .current-setting {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
          }

          .contact-fields {
            grid-template-columns: 1fr;
          }

          .schedule-timeline {
            flex-direction: column;
            gap: 0.25rem;
          }

          .timeline-arrow {
            transform: rotate(90deg);
          }

          .schedule-next {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default PortalSettings;
